/**
 * An attestation's witness apparatus as the Edition prints it: numbered lines
 * ("Wit1:", "Wit2:"), each made of one or more segments printed " | " apart.
 * A segment may be scoped to part of the attestation — "Ῥαφαείν] ↓C" — or
 * quote the text as its witnesses read it — "μὴ θεομάχοι … 252".
 *
 * Stored flat, as `witness_entries`: one entry per segment, with `line` and
 * `position`. These functions keep that list in apparatus order — by line,
 * then position, lines numbered from 0 and positions dense — so the wizard,
 * the renderers and the payload agree on it. The API pairs the entries it
 * saves with the payload's by that order.
 *
 * Entries saved before lines existed carry none; each of them was printed as
 * a line of its own, and still is.
 *
 * Pure: every operation returns a new list.
 */

import { WitnessEntry } from '@/hexapla-core/domains/lemmas/schema'

/** A witness entry as the wizard holds it: saved or not, possibly from before lines existed. */
export type ApparatusEntry = Partial<WitnessEntry>

export interface WitnessLineSegment<E> {
  entry: E
  /** Index of the entry in the apparatus-ordered list. */
  index: number
}

export interface WitnessLine<E> {
  line: number
  segments: Array<WitnessLineSegment<E>>
}

const hasLine = (entry: ApparatusEntry) => entry.line !== undefined && entry.line !== null

/** The entries in apparatus order, without renumbering them. */
export const inApparatusOrder = <E extends ApparatusEntry>(entries: E[]): E[] => {
  const legacy = !entries.some(hasLine)

  return entries
    .map((entry, given) => ({ entry, given, line: entry.line ?? 0, position: entry.position ?? given }))
    .sort((a, b) => (legacy ? 0 : a.line - b.line) || a.position - b.position || a.given - b.given)
    .map(({ entry }) => entry)
}

/** Apparatus order, with lines numbered from 0 and positions dense. */
export const normalizeEntries = <E extends ApparatusEntry>(entries: E[]): E[] => {
  const legacy = !entries.some(hasLine)
  const ordered = inApparatusOrder(entries)
  const lineNumbers = Array.from(new Set(ordered.map((entry) => entry.line ?? 0)))

  return ordered.map((entry, position) => ({
    ...entry,
    line: legacy ? position : lineNumbers.indexOf(entry.line ?? 0),
    position,
  }))
}

export const isNormalized = (entries: ApparatusEntry[]): boolean =>
  normalizeEntries(entries).every((entry, i) => entries[i].line === entry.line && entries[i].position === entry.position)

/** The entries grouped into lines. */
export const witnessLines = <E extends ApparatusEntry>(entries: E[]): Array<WitnessLine<E>> => {
  const lines: Array<WitnessLine<E>> = []

  normalizeEntries(entries).forEach((entry, index) => {
    const last = lines[lines.length - 1]
    if (last && last.line === entry.line) last.segments.push({ entry, index })
    else lines.push({ line: entry.line as number, segments: [{ entry, index }] })
  })

  return lines
}

/** An empty segment on `line`: no scope, no witnesses. */
export const newSegment = (line: number): ApparatusEntry => ({
  scope: null,
  scope_style: 'lemma',
  line,
  position: 0,
  witnesses: [],
  variants: [],
})

/** Appends a line holding one empty segment; `index` is the new segment's. */
export const addLine = <E extends ApparatusEntry>(entries: E[]): { entries: E[]; index: number } => {
  const current = normalizeEntries(entries)
  const line = current.length ? (current[current.length - 1].line as number) + 1 : 0
  const next = [...current, { ...newSegment(line), position: current.length } as E]

  return { entries: next, index: next.length - 1 }
}

/** Adds an empty segment at the end of `line` (a new line if there is none). */
export const addSegment = <E extends ApparatusEntry>(entries: E[], line: number): { entries: E[]; index: number } => {
  const current = normalizeEntries(entries)
  let last = -1
  current.forEach((entry, i) => {
    if (entry.line === line) last = i
  })
  if (last === -1) return addLine(current)

  const next = [...current.slice(0, last + 1), newSegment(line) as E, ...current.slice(last + 1)].map(
    (entry, position) => ({ ...entry, position })
  )

  return { entries: next, index: last + 1 }
}

/** Removes a whole line; later lines move up. */
export const removeLine = <E extends ApparatusEntry>(entries: E[], line: number): E[] =>
  normalizeEntries(normalizeEntries(entries).filter((entry) => entry.line !== line))

/** Removes one segment; a line left empty disappears. */
export const removeSegment = <E extends ApparatusEntry>(entries: E[], index: number): E[] =>
  normalizeEntries(normalizeEntries(entries).filter((_, i) => i !== index))

/**
 * A segment's scope as printed, and whether a "]" closes it (a scope of the
 * lemma's words, not a quoted reading); null when the segment is unscoped.
 */
export const segmentScope = (entry: ApparatusEntry): { text: string; bracket: boolean } | null => {
  const text = (entry.scope || '').trim()
  return text ? { text, bracket: entry.scope_style !== 'reading' } : null
}

/**
 * What prints before a segment's witnesses: "Ῥαφαείν]" for a scope, the text
 * itself for a quoted reading, nothing when the segment is unscoped.
 */
export const segmentLabel = (entry: ApparatusEntry): string => {
  const scope = segmentScope(entry)
  return scope ? `${scope.text}${scope.bracket ? ']' : ''}` : ''
}

export default {
  inApparatusOrder,
  normalizeEntries,
  isNormalized,
  witnessLines,
  newSegment,
  addLine,
  addSegment,
  removeLine,
  removeSegment,
  segmentLabel,
}
